/**
 * Idle-session arithmetic, shared by the internal app and the applicant portal.
 *
 * Deliberately dependency-free — no `@angular/*`, no `rxjs`, no DOM. Everything
 * impure lives in each app's own `SessionIdleService`: the clock, the interval, the
 * activity listeners, the storage, the modal, and the auth service. Those are the
 * parts that legitimately differ (different auth services, different modal
 * components, and the applicant portal is zoneless while the internal app is not),
 * so only the arithmetic is shared, and the arithmetic is identical by definition.
 *
 * This replaced `@ng-idle/core`, which cannot run in a zoneless app: it references
 * the global `Zone` directly, so it throws `ReferenceError: Zone is not defined`
 * where `zone.js` is not loaded. That package's last release was Sept 2024 and
 * upstream is unmaintained, so there was no version to upgrade to.
 */

/** How long the user may be inactive, and how long they then get to respond. */
export interface IdleTimerConfig {
	/** Inactivity before the warning is shown. */
	idleMs: number;
	/** How long the warning counts down before the session is over. */
	warningMs: number;
}

/** Where a session stands, derived fresh from the clock on every evaluation. */
export type IdleDecision =
	/** Still within the idle allowance. Nothing to show. */
	| { phase: 'active' }
	/** Past the idle allowance, inside the response window. */
	| { phase: 'warning'; secondsRemaining: number }
	/** Past both. The session is over. */
	| { phase: 'expired' };

/**
 * Decides the session phase from two timestamps.
 *
 * **Stateless on purpose.** It holds configuration only; `lastActivityAtMs` is owned
 * by the caller, which is what lets the apps keep it in `localStorage` and share one
 * idle clock across every tab on the origin. Activity in one tab therefore resets
 * the others, and dismissing the warning in one tab clears it everywhere — both fall
 * out of reading the same number rather than needing any cross-tab messaging.
 *
 * **Derived, never accumulated.** Each call recomputes from `nowMs`, so no counter
 * can drift or be skipped. A laptop that sleeps through the entire warning window
 * reports `expired` on its first tick after waking, rather than waking into a
 * countdown that already ran out. This is the property `@ng-idle/core` provided via
 * its `IdleExpiry.isExpired()` check (`expiry <= now()`), and it is the reason a
 * plain decrementing counter is not good enough.
 */
export class IdleTimer {
	constructor(private readonly config: IdleTimerConfig) {}

	/**
	 * @param lastActivityAtMs Epoch ms of the last recorded user activity.
	 * @param nowMs Epoch ms now.
	 */
	decide(lastActivityAtMs: number, nowMs: number): IdleDecision {
		const idleForMs = nowMs - lastActivityAtMs;

		// Negative means the clock moved backwards, or another tab wrote a timestamp
		// from a machine whose clock is ahead. Treat it as activity: failing towards
		// "keep working" is right, because the alternative is signing someone out over
		// clock skew they cannot see or fix.
		if (idleForMs < this.config.idleMs) {
			return { phase: 'active' };
		}

		const warningElapsedMs = idleForMs - this.config.idleMs;
		if (warningElapsedMs >= this.config.warningMs) {
			return { phase: 'expired' };
		}

		// Round up so the first tick of the warning reads as the full window rather
		// than one second less, which looks like the countdown started late.
		const secondsRemaining = Math.ceil((this.config.warningMs - warningElapsedMs) / 1000);
		return { phase: 'warning', secondsRemaining };
	}
}
